import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CertifiedBadge } from "@/components/CertifiedBadge";
import { FreshnessGauge } from "@/components/FreshnessGauge";
import { Wind, Droplet, Sparkles, Flame, Activity, ArrowRight, PlayCircle, MapPin } from "lucide-react";
import { CITY_META, FEATURED_CITY_SLUGS } from "@/config/cities";
import RecentCertifiedTicker from "@/components/RecentCertifiedTicker";

export default function Landing() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const tryDemo = async () => {
    setDemoLoading(true);
    try {
      const u = await login("host@test.com", "hostpass123");
      toast.success(`Demo host loaded — welcome, ${u.name}`);
      nav("/host");
    } catch { toast.error("Could not load the demo. Please try signing in manually."); setDemoLoading(false); }
  };
  return (
    <div className="scent-wave">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs tracking-[0.18em] uppercase font-semibold" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-ring" />
              Hospitality Freshness · Verified
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#0A1128]" data-testid="hero-heading">
              See it. <span className="italic text-emerald-600">Smell</span> it. <br />
              <span className="text-[#D4AF37]">Book</span> it.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
              The first freshness verification standard for short-term stays. Real-time air, humidity, and cleaning data on every certified property — so guests never wonder what a room really smells like.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="hero-browse-cta">
                <Button size="lg" className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-8">
                  Explore Certified Stays <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-host-cta">
                <Button size="lg" variant="outline" className="rounded-full border-[#D4AF37] text-[#0A1128] hover:bg-[#F9F3E5] px-8">
                  Certify Your Property
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-pricing-cta">
                <Button size="lg" variant="ghost" className="rounded-full text-[#0A1128] hover:bg-slate-100 px-6">
                  See pricing →
                </Button>
              </Link>
            </div>
            <div className="mt-4">
              <button
                onClick={tryDemo}
                disabled={demoLoading}
                data-testid="hero-try-demo"
                className="inline-flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800 font-medium underline underline-offset-4 decoration-emerald-300 hover:decoration-emerald-600 transition-colors"
              >
                <PlayCircle className="w-4 h-4" />
                {demoLoading ? "Loading demo…" : "Try the live host demo — no signup"}
              </button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <div><span className="font-serif text-2xl text-[#0A1128]">20</span> certified stays live</div>
              <div className="w-px h-6 bg-slate-300" />
              <div><span className="font-serif text-2xl text-[#0A1128]">99</span> peak freshness score</div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src="https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=1400" alt="Certified fresh room" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/50 via-transparent to-transparent" />
              <div className="absolute top-5 left-5">
                <CertifiedBadge tier="premium" compact />
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <div className="glass rounded-2xl p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Aurora Loft · SoHo</div>
                  <div className="font-serif text-xl text-[#0A1128] mt-1">96 / 100 Freshness</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-slate-200 hidden md:block scent-drift">
              <FreshnessGauge score={96} size={140} label="LIVE SCORE" />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">The Standard</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">What we verify.</h2>
          <p className="mt-4 text-slate-600">Five signals of hospitality freshness — combined into one honest score. Simulated today, IoT-powered tomorrow.</p>
        </div>

        <div className="mt-12 grid md:grid-cols-6 gap-6">
          {[
            { icon: Activity, title: "Freshness Monitoring", tag: "Continuous", span: "md:col-span-3 md:row-span-2", desc: "Ambient VOC signals sampled across the stay, benchmarked against a fresh-air baseline." },
            { icon: Wind,     title: "Air Quality",         tag: "PM2.5 · CO₂", span: "md:col-span-3", desc: "Sub-micron particulate & CO₂ trend lines refreshed every minute." },
            { icon: Droplet,  title: "Humidity",            tag: "35–55%",      span: "md:col-span-2", desc: "The hospitality humidity sweet-spot, monitored 24/7." },
            { icon: Flame,    title: "Smoke Detection",     tag: "Instant",     span: "md:col-span-2", desc: "Automatic host alerts on tobacco or combustion signatures." },
            { icon: Sparkles, title: "Cleaning Verified",   tag: "Time-stamped", span: "md:col-span-2", desc: "Every turnover certified with digital cleaning proof." },
          ].map((f, i) => (
            <div key={f.title} data-testid={`feature-${i}`} className={`bg-white rounded-3xl border border-slate-200 p-8 hover:-translate-y-1 hover:shadow-xl transition-transform duration-200 ${f.span}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{f.tag}</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-[#0A1128]">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RECENTLY CERTIFIED — LIVE SOCIAL PROOF TICKER */}
      <RecentCertifiedTicker />

      {/* POPULAR CITIES — SEO INTERNAL LINKING */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="landing-cities">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Where We Verify</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">Popular certified cities.</h2>
          <p className="mt-4 text-slate-600">Every city has its own micro-climate — humidity, allergens, air quality. We certify to each one&apos;s realities. Explore by destination.</p>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {FEATURED_CITY_SLUGS.map((slug) => {
            const c = CITY_META[slug];
            if (!c) return null;
            return (
              <Link
                key={slug}
                to={`/stays/${slug}`}
                data-testid={`landing-city-${slug}`}
                className="group relative rounded-3xl overflow-hidden aspect-[4/5] shadow-md hover:shadow-2xl transition-shadow"
              >
                <img src={c.hero} alt={`Certified stays in ${c.name}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/85 via-[#0A1128]/20 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <div className="flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase text-white/70 font-semibold">
                    <MapPin className="w-3 h-3" />
                    {c.region || c.country}
                  </div>
                  <div className="mt-2 font-serif text-2xl text-white">{c.name}</div>
                  <div className="mt-1 text-white/80 text-xs">{c.tagline}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* BADGE CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="rounded-3xl bg-[#0A1128] overflow-hidden relative">
          <div className="grid md:grid-cols-2 gap-10 p-10 md:p-16 items-center">
            <div>
              <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">For Hosts</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-white tracking-tight">Wear the badge <br /> guests trust.</h2>
              <p className="mt-4 text-slate-300 max-w-md">List with confidence on Airbnb, Vrbo, Booking.com and your own site. Every Certified Fresh listing links back to a verifiable public certification page.</p>
              <div className="mt-8 flex gap-3">
                <Link to="/register" data-testid="badge-cta">
                  <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#c19d2e] text-[#0A1128] px-8">Apply for Certification</Button>
                </Link>
                <Link to="/browse">
                  <Button size="lg" variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-8">See a live listing</Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <CertifiedBadge tier="premium" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
